// The address book: field vocabulary, the seven social ties, and the merge.
//
// Design and reasoning: docs/plan-address-book.md. Schema: sql/schema.sql,
// "Address book". Note this is NOT `contact.ts`, which is the public
// contact-us form and unrelated.
//
// This lives in `shared/` for the same reason the event-type registry does:
// the server enforces these rules and the SPA has to render the same facts, so
// two copies would drift. It matters more here, because `shared/` is the only
// package with a test runner — and `mergeContact()` below is the single
// trickiest rule in the whole feature.
//
// WHAT THIS MODULE DOES NOT DO: it does not decide DISCLOSURE. Whether a field
// may be seen at all is a server-side check against an accepted connection and
// `user_field_shares` (per field AND per tie). By the time anything here runs,
// the caller must already have stripped every field the viewer may not see.
// Treat that as a security boundary: a pure function cannot enforce it.
export const SOCIAL_TIES = [
    'family',
    'friends',
    'communal',
    'business',
    'school',
    'theological',
    'activities',
];
const TIE_LABELS = {
    family: 'Family',
    friends: 'Friends',
    communal: 'Communal',
    business: 'Business',
    school: 'School',
    theological: 'Theological',
    activities: 'Activities',
};
export function socialTieLabel(tie) {
    return TIE_LABELS[tie];
}
export function isSocialTie(value) {
    return SOCIAL_TIES.includes(value);
}
export const CONTACT_FIELD_KEYS = [
    'name_title',
    'name_given',
    'name_preferred',
    'name_surname',
    'name_suffix',
    'company',
    'born_date',
    'phone_cell',
    'phone_home',
    'phone_work',
    'email_personal',
    'email_work',
    'home_address1',
    'home_address2',
    'home_city',
    'home_state',
    'home_zip',
    'home_country',
    'work_address1',
    'work_address2',
    'work_city',
    'work_state',
    'work_zip',
    'work_country',
];
export function isContactFieldKey(value) {
    return CONTACT_FIELD_KEYS.includes(value);
}
/**
 * Fields every connection may see, whatever the sharing matrix says.
 *
 * SocialTie states this outright beside the name field: "Your first name will
 * always be shared." Same floor-versus-choice pattern as `generic` in the
 * event-type picker — a code-defined floor, not rows everyone must remember to
 * tick. Keep this set SMALL: everything in it is a disclosure nobody opted into.
 */
export const ALWAYS_SHARED_FIELDS = ['name_preferred'];
/**
 * How a synced field maps onto the source profile (`site_user_info`).
 *
 * A key absent here simply never resolves on the synced side — `site_user_info`
 * has no work address, for instance, so those stay manual-only. ONE mapping,
 * here, or the server and the SPA will disagree about what "synced" means.
 */
export const SYNCED_FIELD_SOURCE = {
    name_given: 'firstName',
    name_preferred: 'firstName',
    name_surname: 'lastName',
    phone_cell: 'phone',
    home_address1: 'address1',
    home_address2: 'address2',
    home_city: 'city',
    home_state: 'state',
    home_zip: 'zip',
    home_country: 'country',
};
/** Absent, null, or whitespace-only all mean "no value here". */
function present(value) {
    return typeof value === 'string' && value.trim() !== '';
}
/**
 * Compare two stamps. A missing stamp sorts OLDEST, so a value that was never
 * stamped loses to one that was — which is what we want during the window
 * before every write is stamping properly.
 *
 * Stamps are naive UTC `DATETIME` strings ("2026-09-03 14:00:00"), which sort
 * correctly as plain strings because the format is fixed-width and
 * big-endian. Do not "improve" this into Date parsing: these are not instants
 * with zones, and `new Date()` on a naive string is interpreted differently
 * across runtimes.
 */
function newer(a, b) {
    if (!present(a))
        return false;
    if (!present(b))
        return true;
    return a > b;
}
/**
 * Merge the two layers into the ONE record a person actually sees.
 *
 * Storage never merges — the owner's manual layer must survive intact, or notes
 * stop being trustworthy. But nobody should be shown two versions of a phone
 * number and asked to pick, so the VIEW merges.
 *
 * The rule is **most current wins, per FIELD**. Per-record would let one
 * layer's recent edit hide the other layer's older but still-correct values:
 * their new phone in March would bury the address you fixed by hand in January.
 *
 * Ties go to `manual`. It is the owner's own data and the conservative choice,
 * and an exact stamp collision means we have no evidence either way.
 *
 * A field the sharer CLEARS simply stops resolving from the synced side, so the
 * manual value (if any) reappears. The alternative reading — that clearing is a
 * deliberate newer act which should blank the field — is defensible, but it
 * lets someone erase data out of another person's private address book, so it
 * is deliberately not what happens.
 */
export function mergeContact(input) {
    const { manual, manualTimes = {}, synced = {}, syncedTimes = {} } = input;
    const out = {};
    for (const key of CONTACT_FIELD_KEYS) {
        const m = manual[key];
        const s = synced[key];
        const hasM = present(m);
        const hasS = present(s);
        if (hasM && hasS) {
            out[key] = newer(syncedTimes[key], manualTimes[key])
                ? { value: s, source: 'synced' }
                : { value: m, source: 'manual' };
        }
        else if (hasM) {
            out[key] = { value: m, source: 'manual' };
        }
        else if (hasS) {
            out[key] = { value: s, source: 'synced' };
        }
        else {
            out[key] = { value: null, source: 'none' };
        }
    }
    return out;
}
/** Just the values, for callers that do not care where each one came from. */
export function mergedValues(merged) {
    const out = {};
    for (const key of CONTACT_FIELD_KEYS)
        out[key] = merged[key].value;
    return out;
}
// --- Display ---------------------------------------------------------------
/**
 * What to call someone.
 *
 * `name_preferred` wins over `name_given` — Bob, not Robert. SocialTie already
 * behaves this way: its "First Name" row renders `preferredName`, and
 * `givenName` is often empty. A Christmas card addressed to the wrong form of
 * someone's name is precisely the failure this feature exists to prevent.
 */
export function contactDisplayName(fields) {
    const first = present(fields.name_preferred)
        ? fields.name_preferred
        : present(fields.name_given)
            ? fields.name_given
            : '';
    const last = present(fields.name_surname) ? fields.name_surname : '';
    const name = [first, last].filter((p) => p !== '').join(' ');
    if (name !== '')
        return name;
    return present(fields.company) ? fields.company : 'Unnamed contact';
}
/**
 * The envelope line: "Dr. Robert Smith Jr."
 *
 * Deliberately uses `name_given` over `name_preferred` — post goes to the legal
 * name where one is known, falling back to the preferred name when it is not.
 * This is the one place the preference order inverts.
 */
export function contactPostalName(fields) {
    const first = present(fields.name_given)
        ? fields.name_given
        : present(fields.name_preferred)
            ? fields.name_preferred
            : '';
    return [
        present(fields.name_title) ? fields.name_title : '',
        first,
        present(fields.name_surname) ? fields.name_surname : '',
        present(fields.name_suffix) ? fields.name_suffix : '',
    ]
        .filter((p) => p !== '')
        .join(' ');
}
export const CONTACT_SORT_KEYS = [
    'name',
    'company',
    'phone',
    'email',
    'state',
    'country',
];
export function isContactSortKey(value) {
    return CONTACT_SORT_KEYS.includes(value);
}
export const CONNECTION_STATUSES = [
    'pending',
    'accepted',
    'declined',
    'revoked',
];
/**
 * Escape one cell.
 *
 * Two separate jobs, and the second is easy to forget:
 *
 *  1. RFC 4180 quoting — wrap in quotes and double any quote, whenever the
 *     value contains a comma, quote, CR or LF.
 *  2. FORMULA INJECTION. A cell starting `=`, `+`, `-`, `@`, tab or CR is
 *     executed as a formula by Excel, Sheets and LibreOffice. A contact called
 *     `=HYPERLINK(...)` becomes a live formula in whatever the recipient opens.
 *     Prefixing an apostrophe neutralises it; the leading quote is invisible in
 *     a spreadsheet and shows only in a raw text editor, which is the right
 *     trade for a file people open in Excel.
 */
export function csvCell(raw) {
    let v = raw ?? '';
    if (/^[=+\-@\t\r]/.test(v))
        v = `'${v}`;
    if (/[",\r\n]/.test(v))
        return `"${v.replace(/"/g, '""')}"`;
    return v;
}
/** Join rows with CRLF, which is what RFC 4180 and Excel both expect. */
export function toCsv(headers, rows) {
    const lines = [headers.map(csvCell).join(',')];
    for (const r of rows)
        lines.push(r.map(csvCell).join(','));
    return lines.join('\r\n');
}
/**
 * The mail-merge column set: one row per contact, one address block.
 *
 * `postalName` rather than the display name, because post goes to the legal
 * name — "Dr. Robert Smith Jr.", not "Bob". The two are split for exactly this.
 */
export function mailingColumns(which) {
    const p = which === 'home' ? 'home' : 'work';
    const k = (suffix) => `${p}_${suffix}`;
    return [
        { header: 'Name', value: (_f, x) => x.postalName || x.displayName },
        { header: 'Company', value: (f) => f.company ?? '' },
        { header: 'Address 1', value: (f) => f[k('address1')] ?? '' },
        { header: 'Address 2', value: (f) => f[k('address2')] ?? '' },
        { header: 'City', value: (f) => f[k('city')] ?? '' },
        { header: 'State', value: (f) => f[k('state')] ?? '' },
        { header: 'Zip', value: (f) => f[k('zip')] ?? '' },
        { header: 'Country', value: (f) => f[k('country')] ?? '' },
    ];
}
/** Everything, for a backup. Someone handing over twenty years of relationships
 *  needs to see they can get all of it back out. */
export function fullColumns() {
    return [
        { header: 'Display name', value: (_f, x) => x.displayName },
        { header: 'Postal name', value: (_f, x) => x.postalName },
        ...CONTACT_FIELD_KEYS.map((key) => ({
            header: key,
            value: (f) => f[key] ?? '',
        })),
        { header: 'Ties', value: (_f, x) => x.ties.join(' ') },
        { header: 'Lists', value: (_f, x) => x.lists.join(' | ') },
    ];
}
export function buildCsv(columns, rows) {
    return toCsv(columns.map((c) => c.header), rows.map((r) => columns.map((c) => c.value(r.fields, r.extras))));
}
/**
 * Does this contact have enough of an address to post to?
 *
 * A mailing list full of blank rows is worse than a short one: you only notice
 * at the printer. Street line plus city is the minimum worth exporting.
 */
export function hasPostableAddress(fields, which) {
    const p = which === 'home' ? 'home' : 'work';
    const line1 = fields[`${p}_address1`];
    const city = fields[`${p}_city`];
    return (typeof line1 === 'string' && line1.trim() !== '' &&
        typeof city === 'string' && city.trim() !== '');
}
//# sourceMappingURL=addressbook.js.map